// ** Types
import type { FooterSocialLink } from '@/types/layoutProps';
import type {
	MegaMenuLink,
	MenuViewProps,
	MegaMenuColumn,
	MegaMenuContactBlock
} from '@/components/common/MegaMenuOverlay/megaMenuView.types';
import type {
	StrapiMegaMenu,
	StrapiMegaMenuLink,
	StrapiMegaMenuColumn,
	StrapiMegaMenuContact,
	StrapiMegaMenuSocialLink
} from '@/features/ap07/types/ap07-strapi';

// ** Mocks (used as last-resort fallback until CMS is populated)
import { AP07_MEGA_MENU_VIEW } from '@/features/ap07/mocks/ap07.menu.mock';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Maps Strapi social link keys to frontend icon identifiers.
 *
 * Values are normalized to lowercase before lookup, allowing
 * Strapi editors to use:
 *
 * Facebook
 * facebook
 * FACEBOOK
 * LinkedIn
 * X
 * Twitter
 * Xing
 * Threads
 * Discord
 * etc.
 */
const SOCIAL_KEY_MAP: Record<string, string> = {
	facebook: 'facebook',
	instagram: 'instagram',
	linkedin: 'linkedin',
	youtube: 'youtube',
	x: 'x',
	twitter: 'x',
	xing: 'xing',
	discord: 'discord',
	threads: 'threads',
	tiktok: 'tiktok',
	whatsapp: 'whatsapp',
	telegram: 'telegram',
	pinterest: 'pinterest',
	reddit: 'reddit',
	snapchat: 'snapchat',
	twitch: 'twitch',
	github: 'github',
	gitlab: 'gitlab',
	vimeo: 'vimeo',
	spotify: 'spotify',
	mastodon: 'mastodon',
	bluesky: 'bluesky',
	rss: 'rss'
};

const resolveHref = (link: StrapiMegaMenuLink): string | null => {
	if (link.pageLink) return buildPathFromRelationLink(link.pageLink);
	if (link.url) return link.url;
	return null;
};

const mapLink = (link: StrapiMegaMenuLink | null): MegaMenuLink | null => {
	if (!link || link.active === false) return null;
	if (!link.label) return null;
	const href = resolveHref(link);
	if (!href) return null;
	return { label: link.label, href };
};

const mapColumn = (column: StrapiMegaMenuColumn | null): MegaMenuColumn | null => {
	if (!column || column.active === false) return null;
	if (!column.title) return null;
	const items = (column.items ?? []).map(mapLink).filter((i): i is MegaMenuLink => i !== null);
	if (items.length === 0) return null;
	return { title: column.title, items };
};

const mapContact = (
	contact: StrapiMegaMenuContact | null | undefined,
	faqHref: string | undefined
): MegaMenuContactBlock => {
	const fallback = AP07_MEGA_MENU_VIEW.contact;
	if (!contact) {
		return faqHref ? { ...fallback, faqHref } : fallback;
	}
	const addressLines = contact.addressLines?.map((line) => line?.content).filter((s): s is string => Boolean(s)) ?? [];
	return {
		heading: contact.heading ?? fallback.heading,
		addressLines: addressLines.length > 0 ? addressLines : fallback.addressLines,
		phone: contact.phone ?? fallback.phone,
		email: contact.email ?? fallback.email,
		phoneLabel: fallback.phoneLabel,
		emailLabel: fallback.emailLabel,
		...(faqHref ? { faqHref } : {})
	};
};

const mapSocialLink = (link: StrapiMegaMenuSocialLink | null): FooterSocialLink | null => {
	if (!link?.href) return null;
	const key = SOCIAL_KEY_MAP[link.key.trim().toLowerCase()];
	if (!key) return null;
	return {
		key,
		href: link.href,
		iconAlt: key
	};
};

const resolveFaqHref = (data: StrapiMegaMenu): string | undefined => {
	if (data.faqPageLink) return buildPathFromRelationLink(data.faqPageLink);
	if (data.faqUrl) return data.faqUrl;
	return undefined;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Convert the Strapi `mega-menu` single-type payload into `MenuViewProps`.
 *
 * Falls back to {@link AP07_MEGA_MENU_VIEW} only when the whole `megaMenu`
 * payload is missing. Once CMS data exists, editable CMS fields are
 * authoritative: empty `memberLinks`, `columns`, or `socialLinks` stay empty
 * instead of being mixed with mocks. SSO / authenticated filtering is expected
 * to happen server-side (parity with the vertical navigation resolver); this
 * function only re-applies the local `active` flag as a safety net.
 */
export const mapStrapiMegaMenu = (data: StrapiMegaMenu | null | undefined): MenuViewProps => {
	if (!data) return AP07_MEGA_MENU_VIEW;

	const memberLinks = (data.memberLinks ?? []).map(mapLink).filter((l): l is MegaMenuLink => l !== null);
	const menuColumns = (data.columns ?? []).map(mapColumn).filter((c): c is MegaMenuColumn => c !== null);
	const socialLinks = (data.socialLinks ?? []).map(mapSocialLink).filter((s): s is FooterSocialLink => s !== null);

	const faqHref = resolveFaqHref(data);
	const contact = mapContact(data.contact, faqHref);

	// Defaults to `true` so the login button stays visible until an editor
	// explicitly unchecks "Display Member Login Button" in Strapi.
	const showMemberLoginButton = data.showMemberLoginButton ?? true;

	return {
		memberLinks,
		menuColumns,
		contact,
		socialLinks,
		showMemberLoginButton
	};
};
